import loadable from "@loadable/component";
import classnames from "classnames";
import { isLeft, isRight } from "fp-ts/lib/Either";
import { inject } from "mobx-react";
import React from "react";
import type { Store } from "../../../../client/stores";
import { ReadMore } from "../../components/content/ReadMore.component";
import { ShareButtons } from "../../components/integrations/ShareButtons.component";
import { blockMapLegacy } from "../../content-blocks";
import { PayloadResolverLegacy } from "../../decoders/Content.types";
import { BlockBaseLegacy } from "../../decoders/ContentBlocks.types";
import { ItemBlockValueResolver } from "../../decoders/Sites.types";
import type { BlockFragment, Eyebrow, PublishRecordFragment } from "../../generated/webapi.g";
import { getSocialShareInsertion } from "../../lib/utilities";
import { getBlockSchedule } from "../../lib/content";
import { getPaths } from "../../lib/io-ts-validator";
import { logger } from "../../lib/logging";
import { Container } from "../../ui";
import { isScheduled } from "../Block.component";
import "./Content.style.scss";
import { RelatedContent } from "./RelatedContent.component";

const log = logger.getChild("Content");

const ContentFooter = loadable(() => import("./ContentFooter.component"));
const FrontMatter = loadable(() => import("./FrontMatter.component"));

export type ContentProps = {
    content: PublishRecordFragment;
    eyebrow?: Eyebrow;
    block: BlockFragment;
    store?: Store;
};

export const Content = inject("store")(({ content, block, store }: ContentProps) => {
    if (!store || content === null) {
        return null;
    }

    const { site, request } = store;
    const resolvedPayload = PayloadResolverLegacy.decode(content.payload);
    if (isLeft(resolvedPayload)) {
        log.error(`Payload did not validate! ${getPaths(resolvedPayload)}`);
        return null;
    }
    const payload = resolvedPayload.right;

    // Decode block value
    const decoded = ItemBlockValueResolver.decode(block.value);
    const blockValue = isRight(decoded) ? decoded.right : null;

    const { insertSocialButtons, insertSocialButtonsPosition } = getSocialShareInsertion(payload);
    const insertRelatedContentPosition = Math.min(5, Math.floor(payload.blocks.length * 0.66));
    const url = site.getShareUrl(request);

    const containerCss = classnames("content-layout-container", {
        sponsored: payload.is_sponsored,
    });

    return (
        <Container className={containerCss}>
            {!block.tags?.includes("display-hints/no-front-matter") ? (
                <FrontMatter content={content} block={block} />
            ) : null}
            {!insertSocialButtons ? (
                <ShareButtons
                    url={url}
                    title={content.summary.title}
                    desc={content.summary.description}
                    setStyles={false}
                />
            ) : null}
            <ReadMore tags={block.tags} blockValue={blockValue}>
                <article className="content-detail-container">
                    {payload.blocks.map((b, i) => (
                        <React.Fragment key={i}>
                            <ContentBlock block={b} />
                            {insertSocialButtons && i === insertSocialButtonsPosition ? (
                                <ShareButtons
                                    url={url}
                                    title={content.summary.title}
                                    desc={content.summary.description}
                                    setStyles={false}
                                />
                            ) : null}
                            {i === insertRelatedContentPosition ? (
                                <RelatedContent content={content} payload={payload} />
                            ) : null}
                        </React.Fragment>
                    ))}
                </article>
                {payload.is_sponsored ? (
                    <ContentFooter
                        type="sponsored"
                        author={content?.summary.author}
                        siteName={site.sections.general?.name}
                    />
                ) : null}
                {payload.feed_vendor && payload.feed_permalink ? (
                    <ContentFooter
                        type="partner"
                        author={content?.summary.author}
                        siteName={payload.feed_vendor}
                        url={payload.feed_permalink}
                    />
                ) : null}
                <ShareButtons
                    url={url}
                    title={content.summary.title}
                    desc={content.summary.description}
                    setStyles={false}
                />
                <hr className="divider" />
            </ReadMore>
        </Container>
    );
});

(Content as any).defaultProps = {
    adInsertPosition: 6,
    adId: "3331",
};

export const ContentBlock = ({ block }: { block: BlockBaseLegacy }) => {
    if (!block) {
        throw new Error("No Block given to ContentBlock");
    }
    const schedule = getBlockSchedule(block);

    if (schedule && !isScheduled(Date.now(), schedule)) {
        return null;
    }

    const el = React.createElement<BlockBaseLegacy>(blockMapLegacy[block.type], block);
    const blockClass = classnames("content-block", {
        [`type-${block.type}`]: block.type,
        [`embed-type-${block.embed_type}`]: block.embed_type,
        [`provider-${block.provider}`]: block.provider,
    });

    return <section className={blockClass}>{el}</section>;
};

export default Content;
